<script setup>
import { useAbility } from '@casl/vue'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

import { cookieRef } from '@/@layouts/stores/config'

import CommitteeService from '@/services/quality/committee.service'
import { CommitteeStatus, committeeStatusColor, committeeStatusTitle } from '@/enums/quality/committee-status'
import { ReviewFrequency, reviewFrequencyTitle } from '@/enums/quality/review-frequency'
import CommitteeCreateDrawer from '@/views/quality/committee/CommitteeCreateDrawer.vue'

definePage({
  meta: {
    subject: 'committee.menu',
    action: 'committees',
  },
})

const router = useRouter()
const { can } = useAbility()

// ── Filter options ───────────────────────────────────
const statusOptions = Object.values(CommitteeStatus).map(value => ({ title: committeeStatusTitle(value), value }))

const meetingFrequencyOptions = Object.values(ReviewFrequency).map(value => ({
  title: reviewFrequencyTitle(value),
  value,
}))

// Committee types — only the currently active ones populate the filter,
// scoped to the selected unit. Unscoped, UnitScope returns types from every
// unit the user belongs to, so the filter offers options that match nothing
// here.
const selectedUnitId = cookieRef('selectedUnit')

const committeeTypes = ref([])

CommitteeService.listTypes({ active_only: true, unit_id: selectedUnitId.value }, data => {
  committeeTypes.value = data?.data ?? data ?? []
})

// Departments — shared, tenant-wide master, not unit-scoped.
const { data: departmentsData } = useApi(createUrl('/masters/departments', {
  query: { showAll: true, select: ['id', 'name'] },
}))

const departments = computed(() => departmentsData.value?.data ?? departmentsData.value ?? [])

// ── Filters state ────────────────────────────────────
const committeeTypeId = ref(null)
const status = ref(null)
const departmentId = ref(null)
const meetingFrequency = ref(null)
const governingBodyOnly = ref(false)
const noCadenceDeclared = ref(false)
const searchQuery = ref('')

const debouncedSearch = useDebounceFn(val => {
  searchQuery.value = val
  page.value = 1
}, 500)

const filters = computed(() => ({
  committeeTypeId: committeeTypeId.value,
  status: status.value,
  departmentId: departmentId.value,
  meetingFrequency: meetingFrequency.value,
  governingBodyOnly: governingBodyOnly.value,
  noCadenceDeclared: noCadenceDeclared.value,
  search: searchQuery.value,
}))

// ── Table state ──────────────────────────────────────
const itemsPerPage = ref(10)
const page = ref(1)

// NOTE: the committees list endpoint hard-codes orderBy('name') and accepts no
// sort params, so every column is non-sortable rather than exposing sort
// controls that would silently do nothing.
const headers = [
  { title: 'Code', key: 'code', sortable: false },
  { title: 'Name', key: 'name', sortable: false },
  { title: 'Type', key: 'committee_type.name', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Frequency', key: 'meeting_frequency', sortable: false },
  { title: 'Next Meeting', key: 'next_meeting_due', sortable: false },
  { title: 'Members', key: 'member_count', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false, width: 100 },
]

const committees = ref([])
const totalCommittees = ref(0)
const isFetching = ref(false)

const buildQuery = () => ({
  committee_type_id: committeeTypeId.value || undefined,
  status: status.value || undefined,
  department_id: departmentId.value || undefined,
  meeting_frequency: meetingFrequency.value || undefined,
  governing_body_only: governingBodyOnly.value || undefined,
  no_cadence_declared: noCadenceDeclared.value || undefined,
  search: searchQuery.value || undefined,
  per_page: itemsPerPage.value,
  page: page.value,
})

// Monotonic token guarding against out-of-order responses: filter changes,
// search and pagination each fire an independent request with no
// cancellation, so a slow earlier response could otherwise land after a
// faster later one and silently overwrite correct rows with stale ones.
let fetchToken = 0

const fetchCommittees = async () => {
  const token = ++fetchToken

  isFetching.value = true
  await CommitteeService.list(buildQuery(), data => {
    // Ignore a response a newer request has already superseded.
    if (token !== fetchToken) return
    committees.value = data?.data ?? []
    totalCommittees.value = data?.meta?.total ?? 0
  })
  if (token === fetchToken) isFetching.value = false
}

// Any filter change (including debounced search) resets to page 1. If we're
// already on page 1 that reset is a no-op, so fetch directly; otherwise let
// the page watcher below fire the fetch once the reset lands.
watch(filters, () => {
  if (page.value === 1)
    fetchCommittees()
  else
    page.value = 1
})

watch([page, itemsPerPage], fetchCommittees)

fetchCommittees()

// ── Row navigation ───────────────────────────────────
// unplugin-vue-router derives route names from the file path, so
// src/pages/app/quality/committee/[id].vue is named `app-quality-committee-id`.
const goToView = item => {
  router.push({ name: 'app-quality-committee-id', params: { id: item.id } })
}

// ── Add Committee ────────────────────────────────────
const isCreateDrawerVisible = ref(false)

const onCommitteeSaved = () => {
  fetchCommittees()
}
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText class="d-flex flex-wrap gap-4 align-center">
        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 12rem;"
        >
          <AppSelect
            v-model="committeeTypeId"
            :items="committeeTypes"
            item-title="name"
            item-value="id"
            placeholder="Committee Type"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 10rem;"
        >
          <AppSelect
            v-model="status"
            :items="statusOptions"
            item-title="title"
            item-value="value"
            placeholder="Status"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 12rem;"
        >
          <AppSelect
            v-model="departmentId"
            :items="departments"
            item-title="name"
            item-value="id"
            placeholder="Department"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 10rem;"
        >
          <AppSelect
            v-model="meetingFrequency"
            :items="meetingFrequencyOptions"
            item-title="title"
            item-value="value"
            placeholder="Frequency"
            density="compact"
            clearable
          />
        </div>

        <VSwitch
          v-model="governingBodyOnly"
          label="Governing body only"
          color="primary"
          density="compact"
          hide-details
        />

        <VSwitch
          v-model="noCadenceDeclared"
          label="No cadence declared"
          color="primary"
          density="compact"
          hide-details
        />
      </VCardText>
    </VCard>

    <VCard
      variant="flat"
      class="border"
    >
      <VCardItem>
        <template #title>
          <AppTextField
            placeholder="Search..."
            density="compact"
            style="inline-size: 280px;"
            @input="debouncedSearch($event.target.value)"
          >
            <template #prepend-inner>
              <VIcon
                icon="tabler-search"
                size="16"
              />
            </template>
          </AppTextField>
        </template>
        <template #append>
          <div class="d-flex align-center gap-3">
            <VBtn
              v-if="can('create', 'quality.committee')"
              color="primary"
              variant="flat"
              prepend-icon="tabler-plus"
              @click="isCreateDrawerVisible = true"
            >
              Add Committee
            </VBtn>

            <IconBtn
              variant="tonal"
              class="rounded"
              :loading="isFetching"
              @click="fetchCommittees"
            >
              <VIcon icon="tabler-refresh" />
              <VTooltip activator="parent">
                Refresh
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VCardItem>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="committees"
        :items-length="totalCommittees"
        :loading="isFetching"
        class="text-no-wrap"
        hover
        @click:row="(_, { item }) => goToView(item)"
      >
        <!-- Name -->
        <template #item.name="{ item }">
          <div class="d-flex align-center gap-1">
            <span>{{ item.name }}</span>
            <VIcon
              v-if="item.is_governing_body"
              icon="tabler-building-bank"
              size="16"
              class="text-medium-emphasis"
            >
              <VTooltip activator="parent">
                Governing body
              </VTooltip>
            </VIcon>
          </div>
        </template>

        <!-- Type -->
        <template #item.committee_type.name="{ item }">
          {{ item.committee_type?.name ?? '—' }}
        </template>

        <!-- Status -->
        <template #item.status="{ item }">
          <VChip
            :color="committeeStatusColor(item.status)"
            size="small"
            label
          >
            {{ committeeStatusTitle(item.status) }}
          </VChip>
        </template>

        <!-- Frequency -->
        <template #item.meeting_frequency="{ item }">
          <VChip
            v-if="!item.meeting_frequency"
            variant="outlined"
            size="small"
            label
          >
            Not declared
          </VChip>
          <span v-else>{{ reviewFrequencyTitle(item.meeting_frequency) }}</span>
        </template>

        <!-- Next Meeting -->
        <template #item.next_meeting_due="{ item }">
          {{ item.next_meeting_due ? formatDate(item.next_meeting_due) : '—' }}
        </template>

        <!-- Members -->
        <template #item.member_count="{ item }">
          {{ item.member_count ?? '—' }}
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex justify-start gap-1">
            <IconBtn
              variant="tonal"
              class="rounded"
              size="small"
              @click.stop="goToView(item)"
            >
              <VIcon icon="tabler-eye" />
              <VTooltip activator="parent">
                View
              </VTooltip>
            </IconBtn>
          </div>
        </template>

        <!-- Bottom -->
        <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
            <p class="text-sm text-medium-emphasis mb-0">
              {{ totalCommittees }} total records
            </p>
            <VPagination
              v-model="page"
              :length="Math.ceil(totalCommittees / itemsPerPage)"
              :total-visible="5"
            />
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <CommitteeCreateDrawer
      v-model:is-drawer-open="isCreateDrawerVisible"
      :committee="null"
      @saved="onCommitteeSaved"
    />
  </section>
</template>
